<template>
  <div class="fight-rail" :class="{ 'fight-rail--urgent': inactivityUrgent }">
    <span class="fight-rail__eyebrow">{{ $t('fightHud.eyebrow') }}</span>

    <span class="fight-rail__phase">
      {{ $t('fightHud.phaseLabel', {round: roundNumber, phase: phaseLabel}) }}
    </span>

    <!-- The clock is NOT here. It reads as a dial in the middle of the screen
         (`CountDown`, mounted by Location.vue over the character-card band),
         where a fighter is already looking, rather than as a number on a bar
         along the edge. -->

    <!-- One mark per side: the emblem it wears everywhere else, in its slot
         colour, and how many stand under it — nothing else. A coloured dot said
         only "a side"; the emblem is the same sign the establishment screen
         introduced the side by and the board rings its token with, so the bar,
         the board and the choosing screen name a side the same way. The name and the power share moved to the tooltip and
         to the GM's donut — on the rail they were four columns of text per
         side that a player never acts on, crowding out the round, the clock
         and the controls that they do. The viewer's own side keeps its ring
         so "which one am I" survives the compaction. -->
    <ul v-if="sides.length" class="fight-rail__sides">
      <li
          v-for="side in sides"
          :key="side.id"
          class="fight-rail__side"
          :class="{'is-self-side': side.id === selfSideId}"
          :style="sideColorStyle(side.slot)"
          :title="sideTitle(side)"
      >
        <svg class="fight-rail__side-mark" viewBox="0 0 24 24" aria-hidden="true">
          <path :d="sideEmblem(side.slot)"/>
        </svg>
        <span class="fight-rail__side-count">{{ side.member_count }}</span>
      </li>
    </ul>

    <!-- Readiness dots stay grouped under the side they belong to; a
         sideless group (still choosing, or no sides at all) renders exactly
         like the flat list before sides existed. The tactical board drops the
         roster outright: a fight is not bounded to a handful of characters,
         and a thousand dots is a wall of UI over the board itself. The side
         marks above still carry how many stand where. -->
    <ul v-for="group in rosterGroups" :key="group.sideId || 'unassigned'" class="fight-rail__readiness">
      <li
          v-for="participant in group.shown"
          :key="participant.character_id"
          class="fight-rail__dot"
          :class="{
            'is-ready': participant.ended_turn,
            'is-self': participant.character_id === selfCharacterId,
            'is-down': isDown(participant),
            'is-voted': participant.end_vote === true
          }"
          :style="group.side ? sideColorStyle(group.side.slot) : null"
          :title="dotTitle(participant)"
      >
        <span v-if="!compact" class="fight-rail__dot-name">
          {{ participant.name || $t('fightHud.opponentUnnamed') }}
        </span>
        <!-- A downed fighter's dot carries the only thing that still matters
             about them: how close the dice are to taking them out for good. -->
        <span v-if="isDown(participant)" class="fight-rail__dot-saves">
          {{ saveSummary(participant) }}
        </span>
        <!-- The bar toward the next death-save failure, not the tally itself
             — the table can see one more hit finishes them before the dice
             confirm it. -->
        <span v-if="isDown(participant)" class="fight-rail__dot-downed-meter">
          <span
              class="fight-rail__dot-downed-fill"
              :style="{width: downedDamagePercent(participant) + '%'}"
          ></span>
        </span>
      </li>
      <!-- The tail of a roster the bar cannot hold. It is a count, not a
           truncation notice: the dots that survived are the ones a fighter
           acts on — themself, the downed, and whoever has yet to move. -->
      <li v-if="group.hidden" class="fight-rail__dot fight-rail__dot--overflow" :title="overflowTitle(group)">
        +{{ group.hidden }}
      </li>
    </ul>

    <!-- One glyph and a tally. The wording lives in the tooltip because this
         sits on a bar that a long fight has no room to write a sentence on. -->
    <span
        v-if="showInactivity"
        class="fight-rail__inactivity"
        :title="inactivityTitle"
        :aria-label="inactivityTitle"
        role="status"
    >
      <svg class="fight-rail__inactivity-glyph" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/>
        <path d="M12 7v5l3 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <span class="fight-rail__inactivity-tally">{{ inactiveRounds }}/{{ inactivityLimit }}</span>
    </span>

    <!-- Ending early is a vote, not a decision. Once this fighter has cast
         theirs the control reuses the trigger's waiting wording rather than
         inventing a second way to say "everyone else now". Training has
         nobody to vote with — an immortal dummy never agrees to anything —
         so it is replaced outright by the training's own end control. -->
    <div v-if="canVote && !training" class="fight-rail__vote">
      <span v-if="hasVoted" class="fight-rail__vote-waiting">
        {{ $t('actionTriggers.waitingForOpponent') }}
      </span>
      <HudButton
          :type="hasVoted ? ButtonType.CANCEL : ButtonType.SUBMIT"
          @click="$emit('end-vote', !hasVoted)"
      >
        {{ hasVoted ? $t('fightHud.endVoteWithdraw') : $t('fightHud.endFight') }}
        <span class="fight-rail__vote-tally">
          {{ $t('fightHud.endVoteTally', {current: endVoteCount, required: endVoteRequired}) }}
        </span>
      </HudButton>
    </div>

    <!-- Breakthrough is NOT here, and no longer has a control of its own
         anywhere: it is an ordinary special action and reaches the player
         through the action bar with the rest of them. This rail carries the
         round, the sides, and the two fight-wide decisions — end the fight,
         or leave it. -->

    <HudButton
        v-if="training"
        class="fight-rail__forfeit"
        :type="ButtonType.CANCEL"
        @click="$emit('end-training')"
    >
      {{ $t('fightHud.endTraining') }}
    </HudButton>
    <HudButton v-else class="fight-rail__forfeit" :type="ButtonType.CANCEL" @click="$emit('surrender')">
      {{ $t('fightHud.surrender') }}
    </HudButton>
  </div>
</template>

<script>
import HudButton from '@/components/btn/HudButton.vue';
import {ButtonType} from '@/types/ButtonType';
import {fightPhaseLabel} from '@/utils/fightPhase';
import {sideColorVar} from '@/utils/sideColor';
import {sideEmblemPath} from '@/utils/sideEmblems';

/* Names go once the roster passes this; dots per side are capped here. Both
   are display budgets for a single bar, not game rules. */
const COMPACT_ABOVE = 8;
const MAX_DOTS_PER_GROUP = 10;

/* `standing` is the fight's own word for it; `alive` is kept as the fallback
   for a state read before the field existed. */
function participantIsDown(participant) {
  if (participant?.standing !== undefined) return participant.standing === false;
  return participant?.alive === false;
}

/* Who earns one of the scarce dots, most urgent first: the viewer, then anyone
   bleeding out, then whoever the round is still waiting on, then the fighters
   already done. Order within a tier is preserved so the strip does not
   reshuffle itself every tick. */
function byActionability(participants, selfCharacterId) {
  const rank = participant => {
    if (participant.character_id === selfCharacterId) return 0;
    if (participantIsDown(participant)) return 1;
    if (!participant.ended_turn) return 2;
    return 3;
  };
  return participants
      .map((participant, index) => ({participant, index, rank: rank(participant)}))
      .sort((a, b) => (a.rank - b.rank) || (a.index - b.index))
      .map(entry => entry.participant);
}

export default {
  name: 'FightStatusRail',
  components: {HudButton},
  setup() {
    return {ButtonType};
  },
  props: {
    state: {
      type: Object,
      required: true,
    },
    skewMs: {
      type: Number,
      default: 0,
    },
    selfCharacterId: {
      type: String,
      default: null,
    },
    /* The tactical board shows every fighter as a token already, so the rail
       drops its per-fighter roster there and keeps only the round, the side
       marks and the controls. */
    hideRoster: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['surrender', 'end-vote', 'end-training'],
  computed: {
    training() {
      return !!this.state?.training;
    },
    roundNumber() {
      return this.state?.round ?? 1;
    },
    phaseLabel() {
      return fightPhaseLabel(this.state?.phase, this.$t);
    },
    deadline() {
      return this.state?.phase_ends_at || null;
    },
    participants() {
      return this.state?.participants || [];
    },
    sides() {
      return this.state?.sides || [];
    },
    selfSideId() {
      return this.selfParticipant?.side_id ?? null;
    },
    /* Past this many fighters the rail stops writing names beside the dots.
       A name is ~6rem of bar each; a dozen of them and the round, the sides
       and the controls are all pushed off screen. */
    compact() {
      return this.participants.length > COMPACT_ABOVE;
    },
    rosterGroups() {
      return this.hideRoster ? [] : this.participantGroups;
    },
    /* Groups the readiness dots by side, in the same order `sides` arrived
       in, with a trailing group for anyone still unassigned (or every
       participant, when the fight has no sides at all).

       Each group is capped. A fight is not bounded to a handful of characters
       — a position can hold thousands — and the bar is one row, so it shows
       the fighters a viewer can still act on and counts the rest. */
    participantGroups() {
      const sides = this.sides;
      const build = (sideId, side, participants) => {
        const shown = byActionability(participants, this.selfCharacterId).slice(0, MAX_DOTS_PER_GROUP);
        return {
          sideId,
          side,
          participants,
          shown,
          hidden: Math.max(participants.length - shown.length, 0),
        };
      };
      if (!sides.length) {
        return [build(null, null, this.participants)];
      }
      const groups = sides.map(side => build(
          side.id,
          side,
          this.participants.filter(p => p.side_id === side.id),
      ));
      const unassigned = this.participants.filter(
          p => !sides.some(side => side.id === p.side_id),
      );
      if (unassigned.length) {
        groups.push(build(null, null, unassigned));
      }
      return groups;
    },
    /* Everyone has acknowledged but the server has not opened the next phase
       yet: a countdown that keeps running here reads as a stall. */
    resolving() {
      const total = this.state?.participant_count ?? this.participants.length;
      const ended = this.state?.ended_turn_count ?? 0;
      return total > 0 && ended >= total;
    },
    inactiveRounds() {
      return this.state?.inactive_rounds ?? 0;
    },
    inactivityLimit() {
      return this.state?.inactivity_limit ?? 5;
    },
    showInactivity() {
      return this.inactiveRounds > 0;
    },
    inactivityUrgent() {
      return this.showInactivity && this.inactiveRounds >= this.inactivityLimit - 1;
    },
    inactivityTitle() {
      return this.$t('fightHud.inactivityCounter', {
        current: this.inactiveRounds,
        limit: this.inactivityLimit,
      });
    },
    selfParticipant() {
      if (!this.selfCharacterId) return null;
      return this.participants.find(p => p.character_id === this.selfCharacterId) || null;
    },
    /* Only a standing participant may vote — the server answers 403 to anyone
       else, so offering the control to a downed fighter would be a lie. */
    canVote() {
      const me = this.selfParticipant;
      return !!me && me.standing !== false;
    },
    hasVoted() {
      return (this.state?.end_votes || []).includes(this.selfCharacterId);
    },
    endVoteCount() {
      return this.state?.end_vote_count ?? 0;
    },
    endVoteRequired() {
      return this.state?.end_vote_required ?? this.participants.filter(p => p.standing !== false).length;
    },
    failuresToComa() {
      return this.state?.death_save_failures_to_coma ?? 3;
    },
    successesToRecover() {
      return this.state?.death_save_successes_to_recover ?? 3;
    },
  },
  methods: {
    /* The same mark the side wears on the establishment screen and on its
       tokens — one sign per slot, `utils/sideEmblems.js`. */
    sideEmblem(slot) {
      return sideEmblemPath(slot);
    },
    overflowTitle(group) {
      return this.$t('fightHud.rosterOverflow', {
        hidden: group.hidden,
        total: group.participants.length,
      });
    },
    isDown(participant) {
      return participantIsDown(participant);
    },
    /* Under, not merely down. Going into a coma deletes the save rows, so the
       tally reads back as a clean slate — the one thing a comatose fighter
       does not have. */
    isComatose(participant) {
      return participant?.comatose === true;
    },
    saveSummary(participant) {
      if (this.isComatose(participant)) {
        return this.$t('fightHud.comaSummary');
      }
      const save = participant?.death_save || {};
      return this.$t('fightHud.deathSaveTally', {
        failures: save.failures ?? 0,
        failuresLimit: this.failuresToComa,
        successes: save.successes ?? 0,
        successesLimit: this.successesToRecover,
      });
    },
    dotTitle(participant) {
      const name = participant.name || this.$t('fightHud.opponentUnnamed');
      if (this.isDown(participant)) {
        const status = this.isComatose(participant)
            ? this.$t('fightHud.comaStatus')
            : this.$t('fightHud.downStatus');
        return `${name} — ${status} · ${this.saveSummary(participant)}`;
      }
      const ready = participant.ended_turn
          ? this.$t('fightHud.readyStatus')
          : this.$t('fightHud.waitingStatus');
      const voted = participant.end_vote === true ? ` · ${this.$t('fightHud.endVoteAgreed')}` : '';
      return `${name} — ${ready}${voted}`;
    },
    /* Side colour is a fixed 1-5 slot, never a hue picked from the side's own
       data, so a side keeps its colour for the whole fight and across every
       surface that draws it (rail, donut, board rings). Shared with
       `SideSelectionDonut.vue` and `PlayerTacticalMap.vue` via `utils/sideColor.js`
       so the three surfaces can never disagree on what a slot looks like. */
    sideColorVar(slot) {
      return sideColorVar(slot);
    },
    sideColorStyle(slot) {
      return {'--fight-rail-side-color': this.sideColorVar(slot)};
    },
    sidePowerPercent(side) {
      return Math.round((side.power_share ?? 0) * 100);
    },
    sideTitle(side) {
      return `${side.name} — ${this.sidePowerPercent(side)}% · ${side.member_count}`;
    },
    downedDamagePercent(participant) {
      const value = participant?.downed_damage;
      if (value === null || value === undefined) {
        return 0;
      }
      return Math.max(0, Math.min(100, Math.round(value * 100)));
    },
  },
};
</script>

<style scoped>
.fight-rail {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1.25rem;
  background: rgba(6, 14, 20, 0.72);
  border-bottom: 1px solid rgba(0, 255, 255, 0.18);
  backdrop-filter: blur(8px) saturate(0.9);
  -webkit-backdrop-filter: blur(8px) saturate(0.9);
  font-family: 'Orbitron', system-ui, sans-serif;
  color: #d3e0e6;
}

.fight-rail__eyebrow {
  font-size: 0.55rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #7f929c;
}

.fight-rail__phase {
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--hud-cyan, #3ee9f0);
}

/* The side rail: one swatch-and-name chip per side, coloured from the fixed
   1-5 slot palette so a side never changes colour mid-fight. */
.fight-rail__sides {
  display: flex;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.fight-rail__side {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.1rem 0.35rem;
  font-size: 0.58rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #b7c3c9;
  white-space: nowrap;
  /* Transparent by default so only the viewer's own side draws a ring. */
  border: 1px solid transparent;
  border-radius: 0.6rem;
}

/* Stroked, not filled: the emblems are line drawings authored on a 24-unit
   square, and a filled shield at this size is a blob. The glow is the swatch's
   own, kept so the mark still reads as the side's colour at a glance. */
.fight-rail__side-mark {
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
  fill: none;
  stroke: var(--fight-rail-side-color);
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 0.25rem var(--fight-rail-side-color));
}

/* The viewer's own side, marked with a ring rather than the underline the
   side name used to carry — there is no name here to underline any more. */
.fight-rail__side.is-self-side {
  color: #eef4f6;
  border-color: var(--fight-rail-side-color);
  background: rgba(255, 255, 255, 0.06);
}

.fight-rail__side-count {
  font-variant-numeric: tabular-nums;
}

.fight-rail__readiness {
  display: flex;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: hidden;
}

/* The dot is the readiness; the name beside it says whose. Waiting reads dim,
   ready lights up, a downed fighter reads as neither. */
.fight-rail__dot {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.6rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  color: #7f929c;
}

.fight-rail__dot::before {
  content: '';
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: transparent;
  box-shadow: inset 0 0 0 1px rgba(211, 224, 230, 0.45);
}

.fight-rail__dot.is-ready {
  color: var(--hud-cyan, #3ee9f0);
}

.fight-rail__dot.is-ready::before {
  background: var(--hud-cyan, #3ee9f0);
  box-shadow: 0 0 0.5rem rgba(62, 233, 240, 0.7);
}

.fight-rail__dot.is-self .fight-rail__dot-name {
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}

.fight-rail__dot.is-down {
  opacity: 0.55;
  color: #e2726c;
}

.fight-rail__dot.is-down::before {
  background: transparent;
  box-shadow: inset 0 0 0 1px rgba(226, 114, 108, 0.7);
}

/* A vote is a standing decision, not a flash: the dot keeps the readiness
   colour and gains an outline so the two can be read at once. */
.fight-rail__dot.is-voted::before {
  box-shadow: 0 0 0 2px rgba(185, 164, 95, 0.85);
}

.fight-rail__dot-saves {
  font-size: 0.55rem;
  letter-spacing: 0.08em;
  color: #e2726c;
}

/* The bar toward the next death-save failure. Thin on purpose — it sits
   inside a readiness dot, not a panel of its own. */
.fight-rail__dot-downed-meter {
  display: inline-block;
  width: 2.5rem;
  height: 0.2rem;
  border-radius: 0.1rem;
  background: rgba(226, 114, 108, 0.2);
  overflow: hidden;
}

.fight-rail__dot-downed-fill {
  display: block;
  height: 100%;
  background: #e2726c;
  transition: width 0.2s ease;
}

.fight-rail__vote {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-left: auto;
}

.fight-rail__vote-waiting {
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #b9a45f;
  animation: fight-rail-urgent 1.6s ease-in-out infinite;
}

.fight-rail__vote-tally {
  margin-left: 0.4rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}

/* With a vote control present the forfeit button no longer owns the gap. */
.fight-rail__vote + .fight-rail__forfeit {
  margin-left: 0;
}

/* Not a fighter, so it carries none of the readiness colouring — just the
   tally of who did not fit. */
.fight-rail__dot--overflow {
  color: rgba(255, 255, 255, 0.55);
  font-variant-numeric: tabular-nums;
  cursor: help;
}

.fight-rail__inactivity {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #b9a45f;
  cursor: help;
}

.fight-rail__inactivity-glyph {
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
}

.fight-rail__inactivity-tally {
  font-variant-numeric: tabular-nums;
}

.fight-rail--urgent .fight-rail__inactivity {
  color: #e2726c;
  animation: fight-rail-urgent 1.4s ease-in-out infinite;
}

.fight-rail__forfeit {
  margin-left: auto;
  min-width: 5rem;
}

@keyframes fight-rail-urgent {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fight-rail--urgent .fight-rail__inactivity,
  .fight-rail__vote-waiting {
    animation: none;
  }
}
</style>
